import React from 'react'
import { useState } from "react";
import { Button, Grid, Paper, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getAllSpecialties } from '../../Api/Specialty/Specialty.api';
import SearchInput from "../../Components/atoms/Input/SearchInput";
import { ReactComponent as SpecialtyIcon } from "../../Assets/Images/Specialty3.svg";
import {
  Heading1,
  Heading3,
} from "../../Components/atoms/Typography/Heading/Heading";
import { BasicGrid } from "../../Components/organisms/BasicGrid/BasicGrid";
import ConfirmDialog from "../../Components/organisms/ConfirmDialog/ConfirmDialog";
import { permissionValidator, useAuth } from "../../Store/AuthContext";
import useApiData from "../../Utils/Hooks/useApiData";
import useLoadingState from "../../Utils/Hooks/useLodingState";
import useOverlayState from "../../Utils/Hooks/useOverlayState";
import { getColumns } from "./GridColumns";
import { AddSpecialty } from '../../Components/organisms/addSpecialty/addSpecialty';
import {ViewSpecialtyDetails} from '../../Components/organisms/ViewSpecialtyDetails/ViewSpecialtyDetails';
import { deleteSpecialty } from '../../Api/Specialty/Specialty.api';
 
export const SpecialtiesGrid = () => {
const { OverlayState, openOverlay, closeOverlay } = useOverlayState();
const { LoadingState, startLoading, stopLoading } = useLoadingState();
const { state } = useAuth();
const [searchText, setSearchText] = useState("");
const { t } = useTranslation();
  const [pageData, setPageData] = useState({
    currentPage: 1,
    pageCount: 0,
    pageSize: 10,
    totalPages: 0,
    totalRecordsCount: 0,
  });
 
const apiCall = async () => {
    let langCode = new String(state?.lang?.code);
    const response = await getAllSpecialties({
      page: pageData.currentPage,
      size: pageData.pageSize,
      searchText: searchText,
      language: langCode.toUpperCase(),
    });
    if (response.type == 0) {
      return response;
    } else {
      throw new Error(response.message || "Failed to fetch Specialty data");
    }
  };
 
const { isLoading, data, error, refetch } = useApiData({
    apiCall: apiCall,
    dependency: [pageData.currentPage, pageData.pageSize, searchText],
    pagination: {
      isServerSidePagination: true,
      setApiData: (totalRecordsCount, pageSize, pageNumber, pagesCount) => {
        if (
          pageData.currentPage !== pageNumber ||
          pageSize !== pageData.pageSize
        ) {
          setPageData({
            ...pageData,
            currentPage:
              pageData.currentPage !== pageNumber
                ? pageNumber
                : pageData.currentPage,
            pageSize:
              pageSize !== pageData.pageSize ? pageSize : pageData.pageSize,
            totalPages: pagesCount,
            totalRecordsCount: totalRecordsCount,
          });
        } else {
          setPageData({
            ...pageData,
            totalPages: pagesCount,
            totalRecordsCount: totalRecordsCount,
          });
        }
      },
    },
  });

   const deleteSpecialtyHandler = async (specialtyData) => {
    startLoading({ btnName: "delete" });
    
    const payload = {
      specId: specialtyData.specId,
       description: specialtyData.description, 
      language: state?.lang?.code?.toUpperCase(),
    };


 

    try {
      const response = await deleteSpecialty({ specialty: payload });
      
      if (response.type === 0) {
        closeOverlay(); 
        openOverlay({
          OverlayName: "delete-success",
          mode: "success",
          messages: {
            title: t("Delete Specialty"),
            description: t("Specialty deleted successfully.")
          }
        });
      } else if (response.type === 2) {
        // Validation error
        closeOverlay();
        openOverlay({
          mode: "fail",
          messages: {
            title: t("Delete Failed"),
            description: t("This specialty cannot be deleted because it is assigned to one or more doctors. Please reassign them before deleting this specialty.", { ns: "validation" })
          }
        });
      } else {
        // Other errors
        closeOverlay();
        openOverlay({
          mode: "fail",
          messages: {
            title: t("Error"),
            description: t("An unexpected error occurred.", { ns: "validation" })
          }
        });
      }
    } catch (error) {
      console.error("Error deleting specialty:", error);
      closeOverlay();
      openOverlay({
        mode: "fail",
        messages: {
          title: t("Error"),
          description: t("Failed to connect to server.", { ns: "validation" })
        }
      });
    } finally {
      stopLoading();
    }
  };
 

 
  const clickHandler = async (e, data) => {
    if (e === 1) {
      // View Specialty
      openOverlay({
        OverlayName: "View-Specialty",
        record: data,
        mode: "view",
      });
    } else if (e === 2) {
      // Edit Specialty
      openOverlay({
        mode: "update",
        OverlayName: "Edit-Specialty",
        record: data,
      });
    } 
     else if (e === 5) { 
      openOverlay({
        OverlayName: "delete-confirmation",
        record: data,
        messages: {
          title: t("Delete Specialty"),
          description: t("Are you sure you want to delete this specialty? This action cannot be undone."),
        },
      });
    }
  };
 
  return (
    <>
      <Grid container direction={"column"}>
        <Grid item>
          <Stack direction={"row"} justifyContent={"space-between"} mb={2.8}>
            <Heading1 text={"Specialties"} />
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <SearchInput
                width={"376px"}
                searchValue={searchText}
                placeholder={"Specialty ID, Specialty, Created By"}
                getSearchText={(value) => {
                  setSearchText(value);
                  setPageData({ ...pageData, currentPage: 1 });
                }}
              />
 
              <Button
                disableTouchRipple
                variant="lightBlue"
                sx={{ borderRadius: "14px" }}
                onClick={() => {
                  openOverlay({ OverlayName: "New-Specialty",mode: 'create' });
                }}
              >
                <SpecialtyIcon style={{ marginRight: "10px" }} fill="#ffffff" />
                <Heading3
                  text={"New Specialty"}
                  color="#ffffff"
                />
              </Button>
 
            </Stack>
          </Stack>
        </Grid>
 
      <Grid width="100%" item>
                <Paper
                  sx={{
                    padding: "20px 24px",
                    boxShadow: "0px 0px 25px 0px rgba(0, 0, 0, 0.05)",
                    position: "relative",
                    height: "100%",
                  }}
                >
                  <BasicGrid
                    // isError={error}
                    isLoading={isLoading || LoadingState.visible}
                    rowSelect={false}
                    data={data}
                    pageData={pageData}
                    columnArray={getColumns(t, clickHandler)}
                    allowSorting
                    allowPaging
                    field={["EmployeeID", "LastName"]}
                    onRowClick={(rowData) => {
                      openOverlay({
                        OverlayName: "View-Specialty",
                        record: rowData,
                        mode: "view",
                      });
                    }}
                    paginationClick={(pageNumber) => {
                      setPageData({ ...pageData, currentPage: pageNumber });
                    }}
                    pageSizeChange={(selectedPageSize) => {
                      setPageData({ ...pageData, currentPage: 1, pageSize: selectedPageSize });
                    }}
                  />
                </Paper>
              </Grid>  
      </Grid>
     
        {OverlayState.visible && OverlayState.OverlayName === "New-Specialty" && (
            <AddSpecialty
              close={closeOverlay}
              OpenDrawer={OverlayState.visible}
              mode={OverlayState.mode}
              data={OverlayState.record}
              loadSpecialties={refetch}
            />
        )}

        {OverlayState.visible && OverlayState.OverlayName === "Edit-Specialty" && (
            <AddSpecialty
              close={closeOverlay}
              OpenDrawer={OverlayState.visible}
              mode="update"
              data={OverlayState.record}
              loadSpecialties={refetch}
               openParentOverlay={openOverlay}
            />
        )}

{OverlayState.visible &&
        OverlayState.OverlayName === 'create-success' &&
        OverlayState?.messages?.title && (
          <ConfirmDialog
            title={OverlayState.messages.title}
            description={OverlayState.messages.description}
            open={OverlayState.visible}
            close={closeOverlay} 
            onOkClick={() => { 
              refetch();     
              closeOverlay();  
            }}
          />
        )}


        {/* --- Success Dialog for UPDATE --- */}
      {OverlayState.visible &&
        OverlayState.OverlayName === 'update-success' &&
        OverlayState?.messages?.title && (
          <ConfirmDialog
            
            title={OverlayState.messages.title}
            description={OverlayState.messages.description}
            open={OverlayState.visible}
            close={closeOverlay}
            onOkClick={() => { 
              refetch();       
              closeOverlay();  
            }}


          />
        )}


 
        {OverlayState.visible &&
              !OverlayState.OverlayName &&
              OverlayState?.messages?.title && (
                <ConfirmDialog
                  isError={OverlayState.mode === "fail" || OverlayState.mode === 3}
                  title={OverlayState.messages?.title}
                  description={OverlayState.messages?.description}
                  open={OverlayState?.visible}
                  close={() => {
                    closeOverlay();
                  }}
                  onOkClick={() => {
                    closeOverlay();
                  }}
                />
        )}

        

        {OverlayState.visible && OverlayState.OverlayName === "View-Specialty" && (
                <ViewSpecialtyDetails
                  data={OverlayState.record}
                  close={closeOverlay}
                  OpenDrawer={OverlayState.visible}
                  loadDoctors={refetch}
                />
        )}

         {/*  Delete Confirmation Dialog */}
      {OverlayState.visible &&
        OverlayState.OverlayName === "delete-confirmation" && (
          <ConfirmDialog
            title={OverlayState.messages.title}
            description={OverlayState.messages.description}
            open={OverlayState.visible}
            close={closeOverlay}
            onNoClick={closeOverlay}
            onOkTitle={t("Delete")}
            onNoTitle={t("Cancel")}
            onOkClick={() => deleteSpecialtyHandler(OverlayState.record)}
          />
        )}

      {/*  Delete Success Dialog */}
      {OverlayState.visible &&
        OverlayState.OverlayName === "delete-success" &&
        OverlayState?.messages?.title && (
          <ConfirmDialog
            title={OverlayState.messages.title}
            description={OverlayState.messages.description}
            open={OverlayState.visible}
            close={closeOverlay}
            onOkClick={() => {
              refetch(); 
              closeOverlay();
            }}
          />
        )}
 
    </>
  )
}
 